import { cloneElement, useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./Tooltip.css";

const SHOW_DELAY_MS = 200;
const GAP = 8; // between the anchor and the box
const EDGE = 8; // kept clear of the window's edges

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

// The box itself: above the anchor (below it if there's no room), in document.body
const TooltipBox = ({ id, anchor, text }) => {
  const ref = useRef(null);
  const [position, setPosition] = useState(null);

  useLayoutEffect(() => {
    if (!anchor || !ref.current) return;
    const a = anchor.getBoundingClientRect();
    const { width, height } = ref.current.getBoundingClientRect();
    const center = a.left + a.width / 2;
    const left = clamp(center - width / 2, EDGE, window.innerWidth - width - EDGE);
    const above = a.top - height - GAP >= EDGE;
    setPosition({
      left,
      top: above ? a.top - height - GAP : a.bottom + GAP,
      above,
      arrow: clamp(center - left, 10, width - 10),
    });
  }, [anchor, text]);

  return createPortal(
    <div
      ref={ref}
      id={id}
      role="tooltip"
      className={`tooltip ${position?.above === false ? "below" : "above"}`}
      style={{
        left: position?.left ?? 0,
        top: position?.top ?? 0,
        visibility: position ? "visible" : "hidden",
        "--arrow-x": `${position?.arrow ?? 0}px`,
      }}
    >
      {text}
    </div>,
    document.body,
  );
};

// Use in place of title="...": wraps one element (button, span, label...) and shows
// `text` once the pointer has rested on it for 0.2s, or when it gets keyboard focus.
// Hidden again on leave, click, drag, scroll or Escape. The child's own ref is
// replaced, so don't wrap an element that needs one.
const Tooltip = ({ text, children }) => {
  const [anchor, setAnchor] = useState(null);
  const [visible, setVisible] = useState(false);
  const timer = useRef(null);
  const id = useId();

  const hide = useCallback(() => {
    clearTimeout(timer.current);
    setVisible(false);
  }, []);

  const showSoon = () => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setVisible(true), SHOW_DELAY_MS);
  };

  useEffect(() => () => clearTimeout(timer.current), []);

  useEffect(() => {
    if (!visible) return;
    const handleKeyDown = (e) => e.key === "Escape" && hide();
    window.addEventListener("scroll", hide, true);
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("scroll", hide, true);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [visible, hide]);

  if (!text) return children;

  // Run the child's own handler too
  const chain = (name, handler) => (e) => {
    children.props[name]?.(e);
    handler(e);
  };

  return (
    <>
      {cloneElement(children, {
        ref: setAnchor,
        onMouseEnter: chain("onMouseEnter", showSoon),
        onMouseLeave: chain("onMouseLeave", hide),
        onMouseDown: chain("onMouseDown", hide),
        onDragStart: chain("onDragStart", hide),
        onFocus: chain("onFocus", (e) => e.target.matches?.(":focus-visible") && showSoon()),
        onBlur: chain("onBlur", hide),
        "aria-describedby": visible ? id : children.props["aria-describedby"],
      })}
      {visible && anchor && <TooltipBox id={id} anchor={anchor} text={text} />}
    </>
  );
};

export default Tooltip;
